import { SyntheticEvent, useCallback, useMemo, useState } from 'react'
import DialogButton from '../../../Common/Buttons/DialogButton'
import Dialog from '../../../Common/Dialog/Dialog'
import DialogActions from '../../../Common/Dialog/DialogActions'
import DialogContent from '../../../Common/Dialog/DialogContent'
import DialogTitle from '../../../Common/Dialog/DialogTitle'
import { type UserProductDesignStore } from '../../../UserData/_stores/UserProductDesignStore'
import { DeletePaginationItem } from '../../../UserData/_actions/UserDataActions'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from '../../../Common/_hooks/useAppDispatch'
import { StoreProductStore } from '../../../UserData/_stores/Store/StoreProductStore'
import { Alert, Button, Checkbox, Divider, FormControlLabel, Grid, Stack, styled, Typography } from '@mui/material'
import { useAppSelector } from '../../../Common/_hooks/useAppSelector'
import { StoreVariantStore } from '../../../UserData/_stores/Store/StoreVariantStore'
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import SyncProductDialog from './SyncProductDialog'
import { IgnoreStoreVariant, UnignoreStoreVariant, UnsyncStoreVariant, UpdateStoreVariant } from '../../../UserData/_actions/Store/StoreVariantActions'
import { Toast } from '../../../UIData/_actions/UIDataActions'

const Thumbnail = styled('div')(({ theme }) => ({
  outline: '2px solid transparent',
  overflow: 'hidden',
  borderRadius: 15,
  position: 'relative',
  flexShrink: 0,
  transition: 'outline-color 150ms',

  '&.active': {
    outlineColor: theme.palette.secondary.main
  }
}))

const ThumbnailImg = styled('img')({
  height: '90px',
  display: 'block'
})

interface Props {
  storeType: string
  variant: StoreVariantStore
}

export default function VariantRow(props: Props) {
  const dispatch = useAppDispatch()
  const [t] = useTranslation('account-v2')

  const productDesign = useAppSelector(state => props.variant.get('id_product_design') ? state.get('userData').get('productDesigns').get('data').get(String(props.variant.get('id_product_design'))) : undefined)

  const [chooseProductDialogOpened, setChooseProductDialogOpened] = useState(false)

  const onChooseProductDialogOpen = useCallback(() => setChooseProductDialogOpened(true), [])
  const onChooseProductDialogClose = useCallback(() => setChooseProductDialogOpened(false), [])

  const onIgnore = useCallback(() => {
    dispatch(IgnoreStoreVariant(props.storeType, props.variant.get('id_store_product'), props.variant.get('id')))
  }, [props.storeType, props.variant])

  const onUnignore = useCallback(() => {
    dispatch(UnignoreStoreVariant(props.storeType, props.variant.get('id_store_product'), props.variant.get('id')))
  }, [props.storeType, props.variant])

  const onUnsync = useCallback(() => {
    dispatch(UnsyncStoreVariant(props.storeType, props.variant.get('id_store_product'), props.variant.get('id')))
  }, [props.storeType, props.variant])

  const onChooseProductConfirm = useCallback((productDesign: UserProductDesignStore, listOptions: Immutable.Map<number, number>, listBrandingOptions: Immutable.Map<number, number>) => {
    const id_product_design = productDesign.get('id_product_design')
    const id_product = productDesign.get('id_product')

    if (!id_product_design || !id_product) return;

    dispatch(UpdateStoreVariant(
      props.storeType,
      props.variant.get('id_store_product'),
      props.variant.get('id'),
      id_product_design,
      id_product,
      listOptions.valueSeq().toArray(),
    ).set({
      onSuccess: () => {
        setChooseProductDialogOpened(false)
      },
      onFailure: response => {
        dispatch(Toast(response.data?.error === 'store-missing-scope'
          ? t('We are missing permissions for your store. Please reconnect it.')
          : t('Something went wrong!'), undefined, 'error'))
      },
    }))
  }, [props.storeType, props.variant, t])

  return <Grid container>
    <Grid item md={4} sx={{ py: 2, display: 'flex', alignItems: 'flex-start' }}>
      <Stack spacing={0.5}>
        <Typography 
          variant="h4"
          sx={{
            color: props.variant.get('ignored') ? 'warning.main' : (props.variant.get('id_product_design') !== null ? 'text.primary' : 'text.secondary')
          }}
        >{props.variant.get('title')}</Typography>
        <Typography 
          variant="body2"
          sx={{
            color: props.variant.get('ignored') ? 'warning.main' : (props.variant.get('id_product_design') !== null ? 'text.primary' : 'text.secondary')
          }}
        >#{props.variant.get('external_id')}</Typography>
      </Stack>
    </Grid>
    <Grid item md={8} sx={{ py: 2, display: 'flex', alignItems: 'center' }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: '1 1 auto', minWidth: 0, }}>
        <SyncAltIcon
          fontSize="large"
          color={props.variant.get('ignored') ? 'warning' : (props.variant.get('id_product_design') !== null ? 'success' : 'gray')}
        />

        {props.variant.get('ignored') ? <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h3" sx={{ color: 'warning.main' }}>{t('Ignored variant')}</Typography>
          <Button
            variant="outlined"
            color="secondary"
            onClick={onUnignore}
          >{t('Unignore variant')}</Button>
        </Stack> : (props.variant.get('id_product_design') !== null ? <>
          {productDesign ? <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ flex: '1 1 auto', minWidth: 0, }}>
            <Thumbnail>
              <ThumbnailImg src={productDesign.get('thumbnail')} />
            </Thumbnail>
            <Stack spacing={1} sx={{ flex: '1 1 auto', minWidth: 0, }}>
              <Stack spacing={0} sx={{ fontSize: '0.9rem' }}>
                <Typography variant="h4" sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>{productDesign.get('name')}</Typography>

                {productDesign.get('availableOptionTypes').valueSeq().map(optionType => {
                  //Skip branding options
                  //TODO: Replace include-label with id_branding_option_type check when we merge the branding option update
                  if(['printing-sides'].includes(optionType.get('slug')) || ['include-label'].includes(optionType.get('slug'))) return null;

                  const option = optionType.get('options').find(tmpOption => props.variant.get('listOptions').includes(tmpOption.get('id')))

                  return <Stack key={optionType.get('id')} direction="row" justifyContent="space-between">
                    <strong style={{ flex: 1 }}>{optionType.get('name')}</strong>
                    <span style={{ flex: 1 }}>{option?.get('name')}</span>
                  </Stack>
                })}
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={onChooseProductDialogOpen}
                >{t('Change')}</Button>

                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  onClick={onIgnore}
                >{t('Ignore')}</Button>

                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onClick={onUnsync}
                >{t('Unsync')}</Button>
              </Stack>
            </Stack>
          </Stack> : null}
        </> : <Stack direction="row" spacing={1} alignItems="center">
          <Button
            variant="contained"
            color="primary"
            onClick={onChooseProductDialogOpen}
          >{t('Choose product')}</Button>

          <Button
            variant="outlined"
            color="warning"
            onClick={onIgnore}
          >{t('Ignore variant')}</Button>
        </Stack>
        )}
      </Stack>
    </Grid>

    <SyncProductDialog
      productDesignId={props.variant.get('id_product_design') ?? undefined}
      onConfirm={onChooseProductConfirm}
      opened={chooseProductDialogOpened}
      onClose={onChooseProductDialogClose}
    />
  </Grid >
}
